/**
 * The walls' thickness in plan: a filled band over every leaf's square-ended
 * slab (opaque, since the slabs overlap inside the corners), the mitered
 * outer line, a jamb line across the thickness at every cutout edge that
 * meets solid wall, and — where an opening reaches a corner — the solid
 * wall's square end: its cap and its stub's inner edge from the corner. All
 * from the same wall walk the 3D shell extrudes, so the two views agree
 * stroke for stroke. Every wall edge — inner outline, outer line, jambs,
 * stubs — draws at the same 1px weight. Sits under the inner outline and the
 * door / window glyphs, which draw centered within it. Attribute-styled (no
 * CSS classes) so the DXF export reads it like every other plan layer.
 */

import type { FloorPlanRoom, Point } from '@kaiizen/kai-floor-plan';
import { svgPoints, wallBand } from '@kaiizen/kai-floor-plan-editor-core';
import { roomStroke, wallFill } from '../../styles';

export interface WallBandProps {
  s: FloorPlanRoom;
  sp: (p: Point) => Point;
}

export function WallBand({ s, sp }: WallBandProps): React.JSX.Element {
  const band = wallBand(s);
  return (
    <g data-layer='wall-band'>
      {band.fills.map((quad, i) => <polygon key={`f${i}`} points={svgPoints(quad.map(sp))} fill={wallFill} stroke='none' />)}
      {band.outerRuns.map(([a, b], i) => {
        const p = sp(a), q = sp(b);
        return <line key={`o${i}`} x1={p.x} y1={p.y} x2={q.x} y2={q.y} stroke={roomStroke} strokeWidth={1} />;
      })}
      {band.jambs.map(([a, b], i) => {
        const p = sp(a), q = sp(b);
        return <line key={`j${i}`} x1={p.x} y1={p.y} x2={q.x} y2={q.y} stroke={roomStroke} strokeWidth={1} />;
      })}
      {band.stubs.map(([a, b], i) => {
        const p = sp(a), q = sp(b);
        return <line key={`s${i}`} x1={p.x} y1={p.y} x2={q.x} y2={q.y} stroke={roomStroke} strokeWidth={1} />;
      })}
    </g>
  );
}
